import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { TasksSection } from '../../../components/shared/TasksSection';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Separator } from '../../../components/ui/separator';
import { ropaAPI, organizationsAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { RichTextDisplay } from '../../../components/ui/rich-text-editor';
import { PERSONAL_DATA_CATEGORIES, SENSITIVE_DATA_CATEGORIES } from '../../../components/ui/category-accordion-selector';
import {
  ArrowLeft, Edit, Building2, FileText, Users, Shield,
  Database, Share2, Server, Lock, FolderOpen, Clock,
  CheckCircle, AlertCircle, Calendar, Download, FileSpreadsheet,
  UserCheck, MapPin, StickyNote
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  pending_review: { label: 'Pending Review', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  active: { label: 'Active', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  archived: { label: 'Archived', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400' }
};

const LEGAL_BASIS_LABELS = {
  consent: 'Consent',
  contract: 'Contract',
  legal_obligation: 'Legal Obligation',
  vital_interests: 'Vital Interests',
  public_task: 'Public Task',
  legitimate_interests: 'Legitimate Interests',
  not_sure: 'Not sure — DPO to advise',
};

const DATA_SOURCE_LABELS = {
  directly: 'Directly from the person',
  internal: 'From another department within the organisation',
  external: 'From an external source',
};

const THIRD_PARTY_LOCATION_LABELS = {
  within_country: 'Within this country',
  caribbean: 'Within the Caribbean region',
  international: 'Outside the Caribbean (international)',
  mixed: 'Mix of locations',
};

const SYSTEM_HOSTING_LABELS = {
  on_premises: 'On our own servers (on-premises)',
  cloud: 'Cloud / hosted externally',
  not_sure: 'Not sure',
};

const getCategoryNames = (items, categories) => {
  const categoryNames = new Set();
  items.forEach(item => {
    categories.forEach(cat => {
      if (cat.items.includes(item)) {
        categoryNames.add(cat.category);
      }
    });
  });
  return Array.from(categoryNames);
};

// Intake form stores coded values — map them to display labels for the view page
const INTAKE_PERSONAL_DATA_LABELS = {
  basic_identity: 'Basic identity information',
  contact_details: 'Contact details',
  financial_payroll: 'Financial and payroll data',
  transactional: 'Transactional records',
  visual_audio: 'Visual or audio data',
  digital_online: 'Digital / online activity',
  family_info: 'Family information',
  property_details: 'Property details',
  immigration_travel: 'Immigration and travel',
  legal_contractual: 'Legal and contractual',
  other: 'Other',
};

const INTAKE_SENSITIVE_DATA_LABELS = {
  health_medical: 'Health, medical, or disability information',
  biometric: 'Biometric data',
  race_ethnicity: 'Race or ethnic origin',
  religion: 'Religious or philosophical beliefs',
  political_union: 'Political opinions or trade union membership',
  sexual_orientation: 'Sexual orientation or gender identity',
  criminal_records: 'Criminal records or disciplinary history',
};

// Returns display strings for both granular DPO items and intake coded values
const resolveDataLabels = (items, categories, intakeMap) => {
  const granular = getCategoryNames(items, categories);
  if (granular.length > 0) return granular;
  // Fall back to intake coded labels
  return items.map(v => intakeMap[v] || v);
};

const InfoRow = ({ label, value, icon: Icon }) => (
  <div className="flex items-start gap-3 py-3">
    {Icon && <Icon className="h-4 w-4 mt-1 text-muted-foreground flex-shrink-0" />}
    <div className="flex-1 min-w-0">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <div className="text-sm">{value || <span className="text-muted-foreground">-</span>}</div>
    </div>
  </div>
);

export default function ROPAViewPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { isDPO, isComplianceReadOnly: isReadOnly } = useAuth();
  const [record, setRecord] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await ropaAPI.getOne(id);
        setRecord(res.data);
        
        if (isDPO && res.data.organization_id) {
          try {
            const orgRes = await organizationsAPI.getOne(res.data.organization_id);
            setOrganization(orgRes.data);
          } catch (e) {
            // Organization not found
          }
        }
      } catch (error) {
        toast.error('Failed to load record');
        navigate('/compliance/ropa');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isDPO, navigate]);

  const exportToPDF = () => {
    if (!record) return;
    
    const doc = new jsPDF('p', 'mm', 'a4');
    let yPos = 20;
    
    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Processing Activity Record', 14, yPos);
    yPos += 10;
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text(record.process_name || '', 14, yPos);
    yPos += 10;
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, yPos);
    yPos += 15;
    
    doc.setTextColor(0);
    
    // Section: Basic Information
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Basic Information', 14, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    const basicInfo = [
      ['Department', record.department || '-'],
      ['Unit', record.unit || '-'],
      ['Status', STATUS_CONFIG[record.status]?.label || record.status],
      ['Record Types', (record.record_types || []).join(', ') || '-']
    ];
    
    basicInfo.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${label}:`, 14, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(value, 50, yPos);
      yPos += 6;
    });
    
    yPos += 5;
    
    // Purpose
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Purpose of Processing', 14, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const purpose = (record.purpose || '').replace(/<[^>]*>/g, '');
    const purposeLines = doc.splitTextToSize(purpose, 180);
    doc.text(purposeLines, 14, yPos);
    yPos += purposeLines.length * 5 + 10;
    
    // Data Categories
    if (yPos > 250) { doc.addPage(); yPos = 20; }
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Data Categories', 14, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const categories = getCategoryNames(record.personal_data_types || [], PERSONAL_DATA_CATEGORIES);
    doc.text(categories.join(', ') || '-', 14, yPos);
    yPos += 10;
    
    // Legal Basis
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Legal Basis', 14, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(LEGAL_BASIS_LABELS[record.legal_basis] || record.legal_basis || '-', 14, yPos);
    yPos += 10;
    
    // Retention
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Data Retention', 14, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Period: ${record.retention_period || '-'}`, 14, yPos);
    yPos += 6;
    doc.text(`Reason: ${record.retention_reason || '-'}`, 14, yPos);
    yPos += 10;
    
    // Security Measures
    if (yPos > 250) { doc.addPage(); yPos = 20; }
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Security Measures', 14, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const securityMeasures = (record.electronic_security_measures || []).join(', ') || '-';
    const securityLines = doc.splitTextToSize(securityMeasures, 180);
    doc.text(securityLines, 14, yPos);
    yPos += securityLines.length * 5 + 10;
    
    // Sensitive Data
    if (record.collects_sensitive_data) {
      if (yPos > 250) { doc.addPage(); yPos = 20; }
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(180, 100, 0);
      doc.text('Sensitive Data (Special Categories)', 14, yPos);
      yPos += 8;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0);
      const sensitiveCategories = getCategoryNames(record.sensitive_data_types || [], SENSITIVE_DATA_CATEGORIES);
      doc.text(sensitiveCategories.join(', ') || '-', 14, yPos);
    }
    
    doc.save(`ROPA_${record.process_name?.replace(/\s+/g, '_') || 'Record'}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF downloaded');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!record) return null;

  const statusConfig = STATUS_CONFIG[record.status] || STATUS_CONFIG.draft;
  const personalDataCategories = resolveDataLabels(record.personal_data_types || [], PERSONAL_DATA_CATEGORIES, INTAKE_PERSONAL_DATA_LABELS);
  const sensitiveDataCategories = resolveDataLabels(record.sensitive_data_types || [], SENSITIVE_DATA_CATEGORIES, INTAKE_SENSITIVE_DATA_LABELS);

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="ropa-view-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/compliance/ropa')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-2xl font-bold">{record.process_name}</h1>
                <Badge className={cn("font-normal", statusConfig.color)}>
                  {statusConfig.label}
                </Badge>
              </div>
              <p className="text-muted-foreground">
                {record.department}
                {record.unit && ` / ${record.unit}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={exportToPDF}>
              <Download className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
            {!isReadOnly && (
              <Button onClick={() => navigate(`/compliance/ropa/edit/${record.id}`)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Intake Submission Info — shown only for intake-originated records */}
            {record.submitter_email && (
              <Card className="border-blue-200 dark:border-blue-800">
                <CardHeader className="bg-blue-50 dark:bg-blue-950/20">
                  <CardTitle className="flex items-center gap-2 text-base text-blue-800 dark:text-blue-200">
                    <UserCheck className="h-4 w-4" />
                    Submitted via Intake Form
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground mb-0.5">Submitted by</p>
                      <p className="font-medium">{record.submitter_name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-0.5">Job title</p>
                      <p>{record.submitter_job_title || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-0.5">Email</p>
                      <a href={`mailto:${record.submitter_email}`} className="text-blue-600 dark:text-blue-400 hover:underline">
                        {record.submitter_email}
                      </a>
                    </div>
                    {record.submitter_unit && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-0.5">Unit / team</p>
                        <p>{record.submitter_unit}</p>
                      </div>
                    )}
                  </div>
                  {(record.declaration_name || record.declaration_date || record.declaration_signature) && (
                    <div className="border-t pt-3 mt-3 space-y-2">
                      <div className="text-xs text-muted-foreground flex flex-wrap gap-4">
                        {record.declaration_name && <span>Declared by: <span className="text-foreground font-medium">{record.declaration_name}</span></span>}
                        {record.declaration_date && <span>Date: <span className="text-foreground font-medium">{record.declaration_date}</span></span>}
                      </div>
                      {record.declaration_signature && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Signature</p>
                          <p className="font-serif italic text-base text-foreground border-b border-slate-300 dark:border-slate-600 pb-1 max-w-xs">
                            {record.declaration_signature}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Purpose */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <FileText className="h-4 w-4" />
                  Purpose of Processing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <RichTextDisplay content={record.purpose} />
              </CardContent>
            </Card>

            {/* Data Categories */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Database className="h-4 w-4" />
                  Personal Data Categories
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Data Subjects</p>
                    <div className="flex flex-wrap gap-1">
                      {(record.data_subjects || []).map((subject, i) => (
                        <Badge key={i} variant="secondary">{subject}</Badge>
                      ))}
                      {(!record.data_subjects || record.data_subjects.length === 0) && (
                        <span className="text-muted-foreground text-sm">Not specified</span>
                      )}
                    </div>
                  </div>
                  <Separator />
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">
                      Personal Data Types ({personalDataCategories.length} {personalDataCategories.length === 1 ? 'category' : 'categories'})
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {personalDataCategories.map((cat, i) => (
                        <Badge key={i} variant="outline">{cat}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Sensitive Data */}
            {record.collects_sensitive_data && (
              <Card className="border-amber-200 dark:border-amber-800">
                <CardHeader className="bg-amber-50 dark:bg-amber-950/20">
                  <CardTitle className="flex items-center gap-2 text-base text-amber-800 dark:text-amber-200">
                    <AlertCircle className="h-4 w-4" />
                    Sensitive Data Collected
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="flex flex-wrap gap-1">
                    {sensitiveDataCategories.map((cat, i) => (
                      <Badge key={i} variant="warning">{cat}</Badge>
                    ))}
                  </div>
                  {record.sensitive_data_legal_basis && (
                    <p className="text-sm text-muted-foreground mt-3">
                      <span className="font-medium">Legal Basis:</span> {record.sensitive_data_legal_basis}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Data Sharing */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Share2 className="h-4 w-4" />
                  Data Sharing
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground">Internal Sharing</p>
                    <p className={cn("font-medium", record.shares_internally ? "text-amber-600" : "")}>
                      {record.shares_internally ? 'Yes' : 'No'}
                    </p>
                    {record.shares_internally && record.internal_sharing_areas && (
                      <RichTextDisplay content={record.internal_sharing_areas} className="mt-2 text-sm" />
                    )}
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground">External Sharing</p>
                    <p className={cn("font-medium", record.shares_externally ? "text-amber-600" : "")}>
                      {record.shares_externally ? 'Yes' : 'No'}
                    </p>
                    {record.shares_externally && (
                      <>
                        {record.external_sharing_recipients && (
                          <RichTextDisplay content={record.external_sharing_recipients} className="mt-2 text-sm" />
                        )}
                        {record.transfer_location && (
                          <div className="mt-2">
                            <Badge variant="outline" className={cn("text-xs", {
                              'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400': ['local','regional'].includes(record.transfer_location),
                              'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400': record.transfer_location === 'international',
                              'bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400': record.transfer_location === 'regulator',
                            })}>
                              {{ local: 'Local', regional: 'Regional', international: 'International', regulator: 'Local — Regulator' }[record.transfer_location] || record.transfer_location}
                            </Badge>
                          </div>
                        )}
                        {(record.third_party_locations || []).length > 0 && (
                          <div className="mt-2 flex items-start gap-1 flex-wrap">
                            <MapPin className="h-3 w-3 mt-0.5 text-muted-foreground flex-shrink-0" />
                            {record.third_party_locations.map((loc, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {THIRD_PARTY_LOCATION_LABELS[loc] || loc}
                              </Badge>
                            ))}
                          </div>
                        )}
                        {record.has_data_sharing_agreement !== null && (
                          <Badge variant={record.has_data_sharing_agreement ? 'success' : 'destructive'} className="mt-2">
                            {record.has_data_sharing_agreement ? 'Agreement in place' : 'No agreement'}
                          </Badge>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Security */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Lock className="h-4 w-4" />
                  Security Measures
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {(record.electronic_security_measures || []).length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Electronic Security</p>
                    <div className="flex flex-wrap gap-1">
                      {record.electronic_security_measures.map((measure, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{measure}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {record.keeps_paper_records && (record.paper_security_measures || []).length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Paper Record Security</p>
                    <div className="flex flex-wrap gap-1">
                      {record.paper_security_measures.map((measure, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{measure}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            {/* Additional Notes */}
            {record.additional_notes && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <StickyNote className="h-4 w-4" />
                    Additional Notes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-wrap">{record.additional_notes}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Quick Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-0 divide-y">
                {isDPO && organization && (
                  <InfoRow label="Organisation" value={organization.name} icon={Building2} />
                )}
                <InfoRow 
                  label="Legal Basis" 
                  value={LEGAL_BASIS_LABELS[record.legal_basis] || record.legal_basis} 
                  icon={Shield} 
                />
                <InfoRow 
                  label="Retention Period" 
                  value={
                    <div>
                      <p>{record.retention_period}</p>
                      {record.retention_reason && (
                        <p className="text-xs text-muted-foreground mt-1">{record.retention_reason}</p>
                      )}
                    </div>
                  } 
                  icon={Clock} 
                />
                <InfoRow 
                  label="Record Types" 
                  value={(record.record_types || []).map(t => t.charAt(0).toUpperCase() + t.slice(1)).join(', ')} 
                  icon={FolderOpen} 
                />
                <InfoRow
                  label="Data Source"
                  value={
                    (record.data_collection_sources || record.data_collection_source || [])
                      .map(s => DATA_SOURCE_LABELS[s] || s)
                      .join(', ') || null
                  }
                  icon={Users}
                />
              </CardContent>
            </Card>

            {/* Systems */}
            {(record.uses_systems || record.uses_system) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Server className="h-4 w-4" />
                    Systems & Software
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {record.system_name && <p className="font-medium">{record.system_name}</p>}
                  {(record.system_hosting || record.system_location) && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {SYSTEM_HOSTING_LABELS[record.system_hosting] ||
                        (record.system_location === 'cloud'
                          ? `Cloud (${record.cloud_country || 'Location not specified'})`
                          : record.system_location === 'on_premises' ? 'On-Premises' : record.system_location)}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Metadata */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Record Info</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>Created: {new Date(record.created_at).toLocaleDateString()}</span>
                </div>
                {record.updated_at && (
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    <span>Updated: {new Date(record.updated_at).toLocaleDateString()}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Linked Tasks (Spec Section 6: manual-link only for ROPA) */}
        <TasksSection sourceModule="ropa" sourceId={record.id} clientId={record.client_id} entityId={record.entity_id} title="Linked tasks" />
      </div>
    </DashboardLayout>
  );
}
